import { fromJS, Map as ImmutableMap, OrderedMap } from 'immutable';
import getIn from 'transmute/getIn';
import set from 'transmute/set';
import setIn from 'transmute/setIn';

// @ts-expect-error untyped import
import { Filter } from '../config/filters';
const setEditors = set('editors');
// returns Filters from tsTypes/reportConfigTypes, but due to the `SRVFiltersBarReport` type issues mentioned below, we can't use it here
const getFilters = getIn(['config', 'filters']);
const setFilters = setIn(['config', 'filters']);
const TIME_PERIODS = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  QUARTER: 'QUARTER',
  YEAR: 'YEAR'
};
const getModifier = (inclusive, rollForward, timeUnit) => {
  if (inclusive) {
    return `THIS_${timeUnit}`;
  }
  if (rollForward) {
    return `NEXT_${timeUnit}`;
  }
  return `LAST_${timeUnit}`;
};
const canMapRollingDateToReportingFormat = filter => {
  const {
    operator,
    timeUnitCount,
    timeUnit,
    property
  } = filter;
  const isCreateDate = ['createdate', 'hs_createdate'].includes(property);
  const isMappedTimePeriod = Object.keys(TIME_PERIODS).includes(timeUnit);
  return isCreateDate && operator === 'ROLLING_DATE_RANGE' && timeUnitCount === 1 && isMappedTimePeriod;
};
const mapFilterToReportingFormat = filters => {
  const filter = filters.get(0).toJS();
  const {
    property,
    inclusive,
    timeUnit,
    rollForward,
    rollingOffset
  } = filter;
  return Filter({
    property,
    value: {
      rangeType: getModifier(inclusive, rollForward, timeUnit)
    },
    useFiscalYear: rollingOffset && rollingOffset === '__hs__FISCAL_YEAR_OFFSET'
  });
};

// all `report` in this file would need to use the `SRVFiltersBarReport` type, but that is found in reporting-enablement, and we don't want to duplicate the type as it may get out of sync. Can readdress when filters bar is no longer expirimental
const setCreateDateDefaultFilter = (report, dateRangeFilters) => {
  if (dateRangeFilters && !dateRangeFilters.isEmpty()) {
    const reportingFilter = mapFilterToReportingFormat(dateRangeFilters);
    const updatedTo = setFilters(getFilters(report).setIn(['dateRange'], reportingFilter), report);
    return updatedTo;
  }
  const defaultReportDateRangeFilters = getFilters(report).dateRange;
  if (defaultReportDateRangeFilters) {
    const nextReport = setFilters(getFilters(report).setIn(['dateRange', 'value', 'rangeType'], 'ALL'), report);
    return setFilters(getFilters(nextReport).setIn(['dateRange', 'value', 'startDate'], '2006-01-01'), nextReport);
  }
  return report;
};
const buildReportingFilterGroups = filters => {
  const filterGroups = [];
  const seenFilters = new Set();
  const removeDuplicates = filterArray => {
    const duplicatesString = JSON.stringify(filterGroups[0]);
    return filterArray.filter(filter => !duplicatesString.includes(JSON.stringify(filter)));
  };
  filters.forEach(({
    filters: filterArray
  }, index) => {
    const duplicates = [];
    const uniqueFilters = [];
    filterArray.forEach(filter => {
      const filterString = JSON.stringify(filter);
      if (seenFilters.has(filterString)) {
        duplicates.push(filter);
      } else {
        seenFilters.add(filterString);
        uniqueFilters.push(filter);
      }
    });
    if (index === 1) {
      filterGroups.unshift(duplicates);
      filterGroups[1] = removeDuplicates(filterGroups[1]);
    }
    filterGroups.push(uniqueFilters);
  });
  return filterGroups;
};
export const normalizeCrmFilters = filters => {
  const groupedFilters = buildReportingFilterGroups(filters);
  return fromJS(groupedFilters).map(filterGroups => OrderedMap({
    filters: filterGroups.map(filter => Filter(filter))
  }));
};
export const addFiltersToReport = (report, normalizedFilters) => {
  let nextReport = report;
  let dateRangeFilters;
  if (normalizedFilters.size > 1) {
    const quickFilters = normalizedFilters.take(1).getIn([0, 'filters']);
    // Filter still untyped
    const quickFilterEditors = quickFilters.map(filter => ImmutableMap({
      property: filter.get('property'),
      type: 'USER_ADDED_QUICK_FILTER'
    }));
    nextReport = setFilters(getFilters(nextReport).set('custom', quickFilters), nextReport);
    nextReport = setEditors(quickFilterEditors, nextReport);
    nextReport = setFilters(getFilters(nextReport).set('filterGroups', normalizedFilters.rest()), nextReport);
  } else {
    dateRangeFilters = normalizedFilters.getIn([0, 'filters']).filter(canMapRollingDateToReportingFormat);
    const nonDateFilters = normalizedFilters.updateIn([0, 'filters'], filters => filters.filterNot(canMapRollingDateToReportingFormat));
    nextReport = setFilters(getFilters(nextReport).set('filterGroups', nonDateFilters), nextReport);
  }
  nextReport = setCreateDateDefaultFilter(nextReport, dateRangeFilters);
  return nextReport;
};
export const addSearchTermToReport = (report, searchTerm) => {
  if (!searchTerm) {
    return report;
  }
  const existingFilters = getFilters(report);
  const filtersWithSearchTerm = existingFilters.set('query', searchTerm);
  return setFilters(filtersWithSearchTerm, report);
};
export const __TESTABLE__ = {
  canMapRollingDateToReportingFormat,
  mapFilterToReportingFormat
};